import { Component, OnDestroy, OnInit } from '@angular/core';
import { IOtherUserRequest } from '../../../core/interfaces/users';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { UserApiService } from '../../../core/services/user-api.service';
import { UserOtherApiService } from '../../../core/services/user-other-api.service';
import { CustomDatePipe } from '../../../core/pipes/custom-date.pipe';
import { Router } from '@angular/router';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzTableModule } from 'ng-zorro-antd/table';
import { CompanyCodeService } from '../../../core/services/adminService/company-code.service';
import { constantTxt } from '../../../core/constants/constants';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-user-other-manage-request-form',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CustomDatePipe,
    NzButtonModule,
    NzSelectModule,
    NzInputModule,
    NzTableModule,
  ],
  templateUrl: './user-other-manage-request-form.component.html',
  styleUrls: ['./user-other-manage-request-form.component.less'],
})
export class UserOtherManageRequestFormComponent implements OnInit, OnDestroy {
  manageForm!: FormGroup;
  allRequests: IOtherUserRequest[] = [];
  companies: any[] = [];
  buyers: any[] = [];
  purchasingOffices: any[] = [];
  statuses = [
    'ALL',
    'DRAFT',
    'OPEN',
    'IN PROGRESS',
    'PARTIAL QUOTED',
    'CLOSED',
    'CANCELED',
  ];
  noDataAvailable = false;
  attachmentMap: { [key: string]: boolean } = {};
  pageSize = 100;
  pageNumber = 1;
  customNoDataMessage = constantTxt.noDataFound;
  companiesSubscription!: Subscription;
  getOfficesListSubscription!: Subscription;
  getBuyersSubscription!: Subscription;
  searchRequestsSubscription!: Subscription;

  constructor(
    private readonly fb: FormBuilder,
    private readonly userApi: UserApiService,
    private readonly UserOtherApiService: UserOtherApiService,
    private readonly companyCodeService: CompanyCodeService,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    this.manageForm = this.fb.group({
      companyCode: [''],
      buyer: [''],
      purchasingOffice: [''],
      status: ['DRAFT'],
      pnaNo: ['', [Validators.pattern('^\\d+(?:-\\d+)?$')]],
    });
    this.loadInitialData();
  }

  loadInitialData(): void {
    this.companiesSubscription = this.companyCodeService
      .getCompanies()
      .subscribe({
        next: (res) => {
          let data = res.body;
          if (data) {
            this.companies = data;
          }
        },
        error: (error) => {
          console.log('Error occured in fetching companies. Error :', error);
        },
      });

    this.getBuyersSubscription = this.userApi.getBuyers().subscribe({
      next: (res) => {
        let data = res.body;
        if (data) {
          this.buyers = data;
        }
      },
      error: (error) => {
        console.log('Error occured in fetching buyers. Error :', error);
      },
    });

    this.getOfficesListSubscription = this.userApi.getOfficesList().subscribe({
      next: (res) => {
        let data = res.body;
        if (data) {
          this.purchasingOffices = data;
        }
      },
      error: (error) => {
        console.log('Error occured in fetching offices. Error :', error);
      },
    });

    this.getOthersPna();
  }

  getOthersPna() {
    const filter = this.manageForm.value;
    const pnaNoControl = this.manageForm.get('pnaNo');
    const pnaNoValue = pnaNoControl?.value?.trim();
    pnaNoControl?.markAsTouched();
    if (pnaNoValue && pnaNoControl?.invalid) {
      return;
    }

    this.searchRequestsSubscription = this.UserOtherApiService.searchRequests(
      filter
    ).subscribe({
      next: (res: any) => {
        let requests = res.body;
        if (requests) {
          const request = requests.map((data: IOtherUserRequest) => ({
            pnaBuyer: data.pnaBuyer,
            pnaCcn: data.pnaCcn,
            pnaCreateDate: data.pnaCreateDate,
            pnaId: data.pnaId,
            pnaNo: data.pnaNo,
            pnaPurchasing: data.pnaPurchasing,
            pnaStatus: data.pnaStatus,
            pnaUpdateDate: data.pnaUpdateDate,
            pnaYear: data.pnaYear,
            pna_buyer_full_name: data.pna_buyer_full_name,
            pna_requestor_full_name: data.pna_requestor_full_name,
            hasAttachment: data.hasAttachment,
          }));
          this.allRequests = request;
        } else {
          this.allRequests = [];
        }
      },
      error: (error) => {
        this.allRequests = [];
        this.noDataAvailable = true;
        console.log('Error occured in fetching offices. Error :', error);
      },
    });
  }

  // Navigate to view request with clean URL - all data passed through state
  navigateToView(pnaNo: string, pnaYear: string): void {
    const navigationState = {
      mode: 'otherview', // Use 'otherview' mode to indicate this is from other-request page
      role: 'user',
      pnaNo: pnaNo,
      year: pnaYear,
      returnUrl: '/user/other-request',
    };

    this.router.navigate(['/user/view-other-request'], {
      state: navigationState,
    });
  }

  onPageSizeChange(pageSize: number): void {
    this.pageSize = pageSize;
    this.pageNumber = 1;
  }

  onPageIndexChange(pageIndex: number): void {
    this.pageNumber = pageIndex;
  }

  ngOnDestroy(): void {
    this.companiesSubscription?.unsubscribe();
    this.getOfficesListSubscription?.unsubscribe();
    this.getBuyersSubscription?.unsubscribe();
    this.searchRequestsSubscription?.unsubscribe();
  }
}
